import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Badge,
  Button,
  Divider,
  Drawer,
  Empty,
  List,
  Popover,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import {
  CompassOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { openHelpPanel } from '@/help/helpPanelStore';
import type { EduTour } from '@/api/types';
import { getEduFieldHelp } from '@/api/edu';
import { listCapabilityFamilies } from '@/api/capabilities';
import { FALLBACK_FAMILIES } from '@/designer/paletteModel';
import { useTour } from './TourContext';
import { DATA_TOUR, surfaceForPath } from './tourModel';
import {
  authoringMode,
  instrumentationDisabled,
  setAuthoringMode,
  setInstrumentationOptOut,
} from './eduSettings';

const { Text, Paragraph } = Typography;

interface CoverageRow {
  family: string;
  count: number;
  status: 'available' | 'absent';
}

/**
 * "Help / Take the tour" (C4) — the discoverable home for enablement in the top bar. Re-launches the
 * tours available for the current surface, re-offers the flagship first-run, opens a field-help
 * coverage view, and exposes the instrumentation opt-out + authoring toggles. Everything degrades:
 * when the `/v1/edu/*` endpoints are absent the lists are simply empty (never an error).
 */
export function HelpMenu() {
  const location = useLocation();
  const { loadTours, startTour, relaunchFirstRun } = useTour();

  const [open, setOpen] = useState(false);
  const [tours, setTours] = useState<EduTour[] | null>(null);
  const [toursLoading, setToursLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coverage, setCoverage] = useState<CoverageRow[] | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Local mirrors of the persisted flags (init from env+localStorage; setters persist).
  const [optOut, setOptOut] = useState(instrumentationDisabled);
  const [authoring, setAuthoring] = useState(authoringMode);

  const surface = surfaceForPath(location.pathname);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setToursLoading(true);
        loadTours(surface)
          .then((t) => setTours(t))
          .catch(() => setTours([]))
          .finally(() => setToursLoading(false));
      }
    },
    [loadTours, surface],
  );

  const openCoverage = useCallback(() => {
    setDrawerOpen(true);
    setCoverageLoading(true);
    listCapabilityFamilies()
      .then((fs) => (fs.length ? fs : FALLBACK_FAMILIES))
      .catch(() => FALLBACK_FAMILIES)
      .then(async (families) => {
        const rows = await Promise.all(
          families.map(async (f): Promise<CoverageRow> => {
            try {
              const fields = await getEduFieldHelp(f.family);
              return { family: f.family, count: fields.length, status: 'available' };
            } catch {
              return { family: f.family, count: 0, status: 'absent' };
            }
          }),
        );
        setCoverage(rows);
      })
      .finally(() => setCoverageLoading(false));
  }, []);

  const content = (
    <div style={{ width: 268 }}>
      {/* FIRST item (OPSFL-HELP-1 contract): the current surface's guide, same panel as `?`. */}
      <Button
        block
        size="small"
        icon={<FileTextOutlined />}
        style={{ textAlign: 'left', justifyContent: 'flex-start', marginBottom: 6 }}
        onClick={() => {
          setOpen(false);
          openHelpPanel();
        }}
      >
        About this page
      </Button>

      <Divider style={{ margin: '4px 0 10px' }} />

      <div className="ol-section" style={{ marginBottom: 6 }}>
        <CompassOutlined /> Guided tours
      </div>
      {toursLoading ? (
        <div style={{ padding: '8px 0', textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      ) : tours && tours.length > 0 ? (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {tours.map((t) => (
            <Button
              key={t.tourId}
              block
              size="small"
              icon={<RocketOutlined />}
              style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              onClick={() => {
                setOpen(false);
                startTour(t);
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</span>
            </Button>
          ))}
        </Space>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No tours for this screen yet.
        </Text>
      )}

      <Divider style={{ margin: '10px 0' }} />

      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Button
          block
          size="small"
          icon={<RocketOutlined />}
          style={{ textAlign: 'left', justifyContent: 'flex-start' }}
          onClick={() => {
            setOpen(false);
            void relaunchFirstRun();
          }}
        >
          Re-take the first run
        </Button>
        <Button
          block
          size="small"
          icon={<ReadOutlined />}
          style={{ textAlign: 'left', justifyContent: 'flex-start' }}
          onClick={() => {
            setOpen(false);
            openCoverage();
          }}
        >
          Field-help coverage
        </Button>
      </Space>

      <Divider style={{ margin: '10px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Tooltip title="Send anonymous product-usage events (edu.*). No free-text, no PII.">
          <Text style={{ fontSize: 12 }}>Usage instrumentation</Text>
        </Tooltip>
        <Switch
          size="small"
          checked={!optOut}
          onChange={(on) => {
            setInstrumentationOptOut(!on);
            setOptOut(!on);
          }}
          aria-label="Toggle usage instrumentation"
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginTop: 6,
        }}
      >
        <Tooltip title="Show a marker on config fields that have no what/why/example yet.">
          <Text style={{ fontSize: 12 }}>Authoring: show missing help</Text>
        </Tooltip>
        <Switch
          size="small"
          checked={authoring}
          onChange={(on) => {
            setAuthoringMode(on);
            setAuthoring(on);
          }}
          aria-label="Toggle authoring missing-help markers"
        />
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={onOpenChange}
        placement="bottomRight"
      >
        <Button
          type="text"
          aria-label="Help and guided tours"
          data-tour={DATA_TOUR.help}
          icon={<QuestionCircleOutlined />}
        />
      </Popover>

      <Drawer
        title="Field-help coverage"
        placement="right"
        width={360}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
          Every registry-published config field must carry what / why / example before its capability
          can publish (F-ED-03, “no help, no publish”). This lists how many help entries each family
          exposes today.
        </Paragraph>
        {coverageLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : coverage && coverage.length > 0 ? (
          <List
            size="small"
            dataSource={coverage}
            renderItem={(row) => (
              <List.Item>
                <span style={{ fontFamily: 'inherit' }}>{row.family}</span>
                {row.status === 'absent' ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    endpoint not live
                  </Text>
                ) : row.count > 0 ? (
                  <Badge
                    count={`${row.count} field${row.count === 1 ? '' : 's'}`}
                    style={{ background: 'var(--ol-positive)' }}
                  />
                ) : (
                  <Text style={{ color: 'var(--ol-attention)', fontSize: 12 }}>no help authored</Text>
                )}
              </List.Item>
            )}
          />
        ) : (
          <Empty description="No families to report." />
        )}
      </Drawer>
    </>
  );
}
